import { Clock } from "lucide-react";
import { STATUS_FLOW } from "../lib/constants";
import { formatTimeAgo, cn } from "../lib/utils";
import { JobCardBadges, JobCardBody, JobCardActions } from "./jobCard/JobCardParts";
import { tenantLocale, tenantTz } from "../lib/company";

/**
 * Card view for a single job on the Dashboard / Jobs Queue.
 * Visual layout:
 *   header row (left: badges + customer name + body / right: time + assignee)
 *   action row (buttons — hidden when compact)
 *
 * Behavior is split into <JobCardBadges>, <JobCardBody>, <JobCardActions> for
 * readability — no state lives outside this file.
 */
export default function JobCard({ job, trucks, onAssign, onAdvance, onCancel, onEdit, onCardClick, onCancelOnRoute, onBookForLater, compact, dense }) {
  const truck = trucks?.find((t) => t.id === job.assigned_truck_id);
  const isAaPlaceholder =
    job.source === "aa_sms" && (!job.address || job.address === "Awaiting details");

  const flowIdx = STATUS_FLOW.findIndex((s) => s.value === job.status);
  const next = flowIdx >= 0 && flowIdx < STATUS_FLOW.length - 1 ? STATUS_FLOW[flowIdx + 1] : null;

  // When `onCardClick` is supplied, the whole card becomes a click target
  // that opens the detail sheet. Action buttons (Assign / Edit / Cancel /
  // Advance) inside `<JobCardActions>` already stop propagation, so a click
  // on those won't bubble up to this handler.
  const cardClickable = typeof onCardClick === "function";

  return (
    <div
      className={cn(
        "bg-white dark:bg-slate-900 border rounded-md transition-all duration-150",
        dense ? "p-3 h-[232px] flex flex-col overflow-hidden" : "p-4",
        job.priority === "urgent" ? "border-rose-300 dark:border-rose-800" : "border-slate-200 dark:border-slate-800",
        job.source === "external_feed" && job.status === "pending" && "feed-flash",
        // AA cancel-on-route awaiting dispatcher's Charge / No-Charge
        // decision — subtle 2s red pulse so it can't be missed on a
        // busy dashboard without strobing.
        job.charge_decision_needed && "charge-decision-pulse border-2",
        cardClickable && "cursor-pointer hover:shadow-md hover:border-slate-300 dark:hover:border-slate-700 hover:-translate-y-0.5",
      )}
      onClick={cardClickable ? () => onCardClick(job) : undefined}
      data-testid={`job-card-${job.id}`}
    >
      <div className={cn("flex items-start justify-between gap-2", dense && "flex-1 min-h-0 overflow-hidden")}>
        <div className="min-w-0 flex-1">
          <JobCardBadges job={job} isAaPlaceholder={isAaPlaceholder} />
          {job.booked_for && job.status !== "completed" && job.status !== "cancelled" && (
            <div className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5" data-testid={`job-booked-for-${job.id}`}>
              <Clock className="h-3 w-3" /> Booked: {new Date(job.booked_for).toLocaleString(tenantLocale(), { timeZone: tenantTz(), dateStyle: "medium", timeStyle: "short", hourCycle: "h23" })}
            </div>
          )}
          <h4 className={cn("font-display font-bold text-slate-900 mt-2 truncate", dense ? "text-sm" : "text-base")}>
            {job.customer_name}
          </h4>

          {!compact && <JobCardBody job={job} dense={dense} />}
        </div>

        <div className="text-right shrink-0">
          <div className="text-xs text-slate-400 flex items-center gap-1 justify-end">
            <Clock className="h-3 w-3" />
            {formatTimeAgo(job.created_at)}
          </div>
          {truck && (
            <div className="mt-2 text-xs">
              <div className="uppercase-label" style={{ fontSize: 10 }}>Assigned</div>
              <div className="font-display font-bold text-slate-900 flex items-center gap-1 justify-end">
                {truck.name}
                {job.queue_position && (
                  <span className="bg-blue-100 text-blue-800 text-[10px] font-black px-1.5 py-0.5 rounded">
                    #{job.queue_position}
                  </span>
                )}
                {/* Feb 2026 — small pip when the driver has tapped
                    "Open Partner App" for this job. Dispatcher glance-
                    scan: "yes, driver has looked at FR / AA". No content,
                    just a coloured dot with a title-tooltip. */}
                {job.partner_opened_at && (
                  <span
                    className="ml-0.5 h-2 w-2 rounded-full bg-emerald-500 inline-block"
                    title={`Opened ${
                      (job.source === "servicenow_api" || job.source === "servicenow_image")
                        ? "First Rescue"
                        : (job.source === "aa_workforms" || job.source === "aa_sms")
                          ? "AA Workforms" : "partner app"
                    } at ${new Date(job.partner_opened_at).toLocaleTimeString(tenantLocale(), { timeZone: tenantTz(), hour: "2-digit", minute: "2-digit", hourCycle: "h23" })}`}
                    data-testid={`job-partner-opened-pip-${job.id}`}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {!compact && (
        <JobCardActions
          job={job}
          next={next}
          isAaPlaceholder={isAaPlaceholder}
          dense={dense}
          onAssign={onAssign}
          onAdvance={onAdvance}
          onCancel={onCancel}
          onEdit={onEdit}
          onCancelOnRoute={onCancelOnRoute}
          onBookForLater={onBookForLater}
        />
      )}
    </div>
  );
}
